import { TenantCheck } from '@/app/[locale]/components/TenantCheck';
import { TenantDropdown } from '@/app/[locale]/components/TenantDropdown/TenantDropdown';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useTenantApi } from '@/hooks/useTenantApi';
import { useRouter } from '@/i18n/routing';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';
import React, { useRef, useState } from 'react';

export const TenantSelector = () => {
  const router = useRouter();
  const t = useTranslations();

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showTenantDropdown, setShowTenantDropdown] = useState(false);

  const { getTenants, switchCurrentTenant } = useTenantApi();
  const { data: tenantData, isFetching: isLoadingTenants } = getTenants();

  useClickOutside(dropdownRef, () => {
    setShowTenantDropdown(false);
  });

  const { tenants = [], activeTenant } = tenantData ?? {};

  if (tenants?.length <= 0) {
    return null;
  }

  const isCentralTenant = !activeTenant;

  const getTenantName = () =>
    !isCentralTenant ? activeTenant?.name : t('navigation_holding');

  const tenantName = isLoadingTenants ? '...' : getTenantName();

  const onClickTenant = async (key: string | undefined) => {
    router.push('/customer');
    const { tenant } = await switchCurrentTenant.mutateAsync({
      tenantId: key ?? null,
    });
    localStorage.setItem('activeTenant', JSON.stringify(tenant));
    // User cache invalidation and refetch is handled in useTenantApi.onSuccess
    setShowTenantDropdown(false);
    router.refresh();
  };

  return (
    <>
      <TenantCheck tenant={activeTenant} />
      <div ref={dropdownRef}>
        <button
          disabled={tenants?.length < 1 || isLoadingTenants}
          onClick={() => setShowTenantDropdown(!showTenantDropdown)}
          className={cn(
            'min-h-[100px] rounded-[6px] px-[14px] py-[15px] text-center',
            {
              'bg-colors-backgrounds-neutral-light': !isCentralTenant,
              'bg-colors-backgrounds-error-accent':
                isCentralTenant && !isLoadingTenants,
            },
          )}
        >
          <span
            className={cn(
              'label-xsmall rotate-180 [writing-mode:vertical-lr]',
              {
                'text-colors-content-primary': !isCentralTenant,
                'text-colors-content-error':
                  isCentralTenant && !isLoadingTenants,
              },
            )}
          >
            {tenantName}
          </span>
        </button>
        {showTenantDropdown && (
          <TenantDropdown
            tenants={tenants}
            selectedTenantId={activeTenant?.id ?? undefined}
            /* eslint-disable-next-line @typescript-eslint/no-misused-promises */
            onClickTenant={onClickTenant}
          />
        )}
      </div>
    </>
  );
};
